import React from "react";
import { useTranslation } from "react-i18next";
import { MediaSortKey } from "../common/QueryTypes";
import { FilterIcon } from "../common/ProjectIcons";
import { DeploymentResponseDto } from "../common/ObjectTypes";
import { ReviewFilterState, ReviewSortState } from "./mediaQueryBuilder";

export type FilterDimension =
  | "deployment"
  | "location"
  | "sensor"
  | "reviewStatus"
  | "date"
  | "confidence"
  | "species"
  | "observationType"
  | "inferenceState";

export type FilterChip = {
  dimension: FilterDimension;
  label: string;
  value: string;
};

/**
 * Builds the active-filter chip list shown in the queue header and, as a
 * count, on the page-level "Filters" button — a single source of truth so
 * the two can't disagree about how many filters are active.
 * @param filterState the current advanced filter state
 * @param selectedDeployments the currently selected deployment scope
 * @param t the translation function (passed in rather than called via a
 *   hook, since this isn't a component)
 * @returns the active-filter chips, in display order
 */
export const buildFilterChips = (
  filterState: ReviewFilterState,
  selectedDeployments: DeploymentResponseDto[],
  t: (key: string, options?: Record<string, unknown>) => string,
): FilterChip[] => {
  const reviewStatusLabel = (v: string) =>
    v === "unreviewed" ? t("awaiting review") : t("complete");

  // Inference-state enum values are snake_case; the translation keys use the
  // space-separated form, so map explicitly rather than passing the raw value.
  const inferenceStateLabel = (v: string): string =>
    ({
      no_run: t("no run"),
      in_progress: t("in progress"),
      completed: t("completed"),
      failed: t("failed"),
    })[v] ?? v;

  // Each chip carries the label prefix separately from the value so we can
  // render the label in the uppercase caption style shown in the mockup.
  const chips: FilterChip[] = [];
  if (selectedDeployments.length === 1)
    chips.push({
      dimension: "deployment",
      label: t("Deployment"),
      value: selectedDeployments[0].deployment_name,
    });
  else if (selectedDeployments.length > 1)
    chips.push({
      dimension: "deployment",
      label: t("Deployment"),
      value: `(${selectedDeployments.length})`,
    });
  if (filterState.locationIds.length > 0)
    chips.push({
      dimension: "location",
      label: t("Location"),
      value: `(${filterState.locationIds.length})`,
    });
  if (filterState.sensorIds.length > 0)
    chips.push({
      dimension: "sensor",
      label: t("Sensor"),
      value: `(${filterState.sensorIds.length})`,
    });
  if (filterState.reviewStatus)
    chips.push({
      dimension: "reviewStatus",
      label: t("Status"),
      value: reviewStatusLabel(filterState.reviewStatus),
    });
  if (filterState.captureTimeFrom || filterState.captureTimeTo)
    chips.push({
      dimension: "date",
      label: t("Date"),
      value: `${filterState.captureTimeFrom ?? "…"} → ${
        filterState.captureTimeTo ?? "…"
      }`,
    });
  if (filterState.confidenceMin !== null || filterState.confidenceMax !== null)
    chips.push({
      dimension: "confidence",
      label: t("AI Confidence"),
      value: `${Math.round(
        (filterState.confidenceMin ?? 0) * 100,
      )}% – ${Math.round((filterState.confidenceMax ?? 1) * 100)}%`,
    });
  if (filterState.predictedTaxonIds.length > 0)
    chips.push({
      dimension: "species",
      label: t("Species"),
      value: `(${filterState.predictedTaxonIds.length})`,
    });
  if (filterState.observationType)
    chips.push({
      dimension: "observationType",
      label: t("Observation"),
      value: t(filterState.observationType),
    });
  if (filterState.inferenceState)
    chips.push({
      dimension: "inferenceState",
      label: t("Inference"),
      value: inferenceStateLabel(filterState.inferenceState),
    });
  return chips;
};

type GridDensity = "S" | "M" | "L";

type ReviewQueueHeaderProps = {
  filterState: ReviewFilterState;
  sortState: ReviewSortState;
  gridDensity: GridDensity;
  selectedDeployments: DeploymentResponseDto[];
  onOpenBuilder: () => void;
  onClearFilter: (dimension: FilterDimension) => void;
  onSortChange: (key: MediaSortKey) => void;
  onToggleCaptureDirection: () => void;
  onReshuffle: () => void;
  onDensityChange: (density: GridDensity) => void;
  /**
   * Rendered at the left of the density row. The review page passes its
   * bulk-select bar here so the two controls share one line instead of stacking;
   * omit it and the density block simply sits alone on the right.
   */
  bulkActions?: React.ReactNode;
};

/**
 * Results-page header: dismissible active-filter chips, the sort control
 * (time / confidence / random), and the S/M/L grid-density toggle. Presentational —
 * all state lives in ManageReviewPage.
 */
const ReviewQueueHeader: React.FC<ReviewQueueHeaderProps> = ({
  filterState,
  sortState,
  gridDensity,
  selectedDeployments,
  onOpenBuilder,
  onClearFilter,
  onSortChange,
  onToggleCaptureDirection,
  onReshuffle,
  onDensityChange,
  bulkActions,
}) => {
  const { t } = useTranslation();

  const chips = buildFilterChips(filterState, selectedDeployments, t);

  const sortButton = (key: MediaSortKey, label: string) => {
    const active = sortState.key === key;
    return (
      <button
        type="button"
        onClick={() => onSortChange(key)}
        className={`px-1 py-1 text-sm transition duration-300 ${
          active
            ? "font-bold text-storm-blue dark:text-primary-off-white"
            : "text-off-black-light dark:text-greyed-text hover:text-storm-blue dark:hover:text-primary-off-white"
        }`}
        data-testid={`sort-${key}`}
      >
        {label}
      </button>
    );
  };

  const densityButton = (value: GridDensity) => {
    const active = gridDensity === value;
    return (
      <button
        type="button"
        onClick={() => onDensityChange(value)}
        aria-label={t("Grid density {{size}}", { size: value })}
        aria-pressed={active}
        className={`px-2 py-1 text-sm rounded transition duration-300 ${
          active
            ? "bg-storm-blue text-primary-off-white dark:bg-sky-blue dark:text-storm-blue"
            : "bg-off-white-dark dark:bg-storm-blue text-off-black-light dark:text-primary-off-white"
        }`}
        data-testid={`density-${value}`}
      >
        {value}
      </button>
    );
  };

  const captureActive =
    sortState.key === "capture_time_asc" ||
    sortState.key === "capture_time_desc";

  return (
    <div className="mb-4">
      <div className="flex flex-row flex-wrap items-center justify-between gap-2">
        <div className="flex flex-row flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onOpenBuilder}
            className="flex items-center gap-1 dark:bg-storm-blue bg-off-white-dark p-2 rounded text-sm text-off-black-dark dark:text-primary-off-white transition duration-300"
            aria-label={t("Edit filters")}
            data-testid="open-filter-builder"
          >
            <FilterIcon />
            <span>{t("Filters")}</span>
            {chips.length > 0 && (
              <span className="ml-1 px-1.5 rounded-full bg-sky-blue text-storm-blue text-xs font-bold">
                {chips.length}
              </span>
            )}
          </button>
          {chips.map((chip) => (
            <span
              key={chip.dimension}
              className="flex items-center gap-2 rounded bg-off-white-dark dark:bg-storm-blue px-2 py-1 text-sm text-off-black-dark dark:text-primary-off-white transition duration-300"
              data-testid={`chip-${chip.dimension}`}
            >
              <span className="text-xs font-medium uppercase tracking-wider text-off-black-light dark:text-greyed-text">
                {chip.label}
              </span>
              <span>{chip.value}</span>
              <button
                type="button"
                onClick={() => onClearFilter(chip.dimension)}
                aria-label={t("Remove {{filter}} filter", {
                  filter: chip.label,
                })}
                className="ml-1 text-off-black-light dark:text-greyed-text hover:text-storm-blue dark:hover:text-sky-blue"
              >
                ×
              </button>
            </span>
          ))}
        </div>

        <div className="flex flex-row items-center">
          <span className="text-sm lowercase text-off-black-light dark:text-greyed-text mr-2">
            {t("Sort")}:
          </span>
          <span className="flex items-center">
            {sortButton(
              sortState.key === "capture_time_desc"
                ? "capture_time_desc"
                : "capture_time_asc",
              t("time"),
            )}
            {captureActive && (
              <button
                type="button"
                onClick={onToggleCaptureDirection}
                aria-label={t("Toggle capture-time sort direction")}
                className="text-storm-blue dark:text-sky-blue-light"
                data-testid="sort-time-direction"
              >
                {sortState.key === "capture_time_desc" ? "↑" : "↓"}
              </button>
            )}
          </span>
          <span
            aria-hidden="true"
            className="text-off-black-light dark:text-greyed-text mx-1"
          >
            |
          </span>
          {sortButton("confidence_desc", t("confidence"))}
          <span
            aria-hidden="true"
            className="text-off-black-light dark:text-greyed-text mx-1"
          >
            |
          </span>
          <span className="flex items-center">
            {sortButton("random", t("random"))}
            {sortState.key === "random" && (
              <button
                type="button"
                onClick={onReshuffle}
                aria-label={t("Reshuffle random sample")}
                className="text-storm-blue dark:text-sky-blue-light"
                data-testid="reshuffle"
              >
                ↻
              </button>
            )}
          </span>
        </div>
      </div>

      {/* Bulk-select controls left, density right. The "N assets match filters"
          caption that used to share this row is gone — the Total Assets stat
          tile above already carries that number, and saying it twice only
          invited the two to disagree. `justify-between` still parks density on
          the right when there is no `bulkActions` node to balance it. */}
      <div className="flex flex-row flex-wrap items-center justify-between gap-2 mt-2">
        <div className="flex flex-row items-center">{bulkActions}</div>
        <div className="flex flex-row items-center gap-1">
          <span className="text-sm text-off-black-light dark:text-greyed-text mr-2">
            {t("density")}
          </span>
          {densityButton("S")}
          {densityButton("M")}
          {densityButton("L")}
        </div>
      </div>
    </div>
  );
};

export default ReviewQueueHeader;
